import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, FormControlLabel,
  Checkbox, Paper, Grid, Alert
} from '@mui/material';
import { CreateVisit, GetLobbyConfig, GetReasons } from '../../../api';

const CheckIn = ({ user, access }) => {
  const [config, setConfig] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({
    ownerName: '', petName: '', phone: '', email: '',
    parkingSpace: '', vehicle: '', reasonId: '', subreasonId: '',
    comment: '', payment: false, pickup: false, answers: {},
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const orgId = access?.org?.id;

  useEffect(() => {
    if (!orgId) return;
    GetLobbyConfig(orgId).then(setConfig).catch(() => {});
    GetReasons(orgId).then(setReasons).catch(() => {});
  }, [orgId]);

  const selectedReason = reasons.find(r => r.id === form.reasonId);
  const subreasons = selectedReason?.subreasons?.filter(s => s.active) || [];

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setAnswer = (qId, value) => setForm(f => ({ ...f, answers: { ...f.answers, [qId]: value } }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    try {
      await CreateVisit({ ...form, org: orgId, source: 'manual', createdBy: user.id });
      setSuccess(true);
      setForm({ ownerName: '', petName: '', phone: '', email: '', parkingSpace: '', vehicle: '', reasonId: '', subreasonId: '', comment: '', payment: false, pickup: false, answers: {} });
    } catch (e) {
      setError(e.response?.data?.errors || 'Failed to create visit');
    }
  };

  const questions = config?.questions?.filter(q => q.active) || [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Manual Check In</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>Visit added to queue.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Owner Name" value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Pet Name" value={form.petName} onChange={e => set('petName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Parking Space" value={form.parkingSpace} onChange={e => set('parkingSpace', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Vehicle" value={form.vehicle} onChange={e => set('vehicle', e.target.value)} />
            </Grid>
            {reasons.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Reason" value={form.reasonId} onChange={e => set('reasonId', e.target.value)}>
                  <MenuItem value="">— Select —</MenuItem>
                  {reasons.filter(r => r.active).map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {subreasons.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Subreason" value={form.subreasonId} onChange={e => set('subreasonId', e.target.value)}>
                  <MenuItem value="">— Select —</MenuItem>
                  {subreasons.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {questions.map(q => (
              <Grid item xs={12} sm={6} key={q.id}>
                {q.type === 'yes_no' ? (
                  <FormControlLabel
                    control={<Checkbox checked={!!form.answers[q.id]} onChange={e => setAnswer(q.id, e.target.checked)} />}
                    label={q.label}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={q.label}
                    required={q.required}
                    multiline={q.type === 'long_text'}
                    rows={q.type === 'long_text' ? 3 : 1}
                    value={form.answers[q.id] || ''}
                    onChange={e => setAnswer(q.id, e.target.value)}
                  />
                )}
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Comment" multiline rows={2} value={form.comment} onChange={e => set('comment', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel control={<Checkbox checked={form.payment} onChange={e => set('payment', e.target.checked)} />} label="Payment needed" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel control={<Checkbox checked={form.pickup} onChange={e => set('pickup', e.target.checked)} />} label="Pickup" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" type="submit">Add to Queue</Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CheckIn;
